import { IconEdit, IconEye, IconTrash } from "@tabler/icons-react";
import { Anchor, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { purchaseOrderApi } from "apis/purchaseOrderApi";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import {
  PURCHASE_ORDER_STATUS_COLOR,
  PURCHASE_ORDER_STATUS_LABEL,
} from "constants/common/purchaseOrder";
import { CommonParamsType } from "constants/types/common";
import { PurchaseOrderResponseType } from "constants/types/purchaseOrder";
import { formatCurrency } from "utils/helpers/format";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FC, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePurchaseOrders } from "utils/hooks/request/usePurchaseOrder";
import { Loading } from "components";
import { PURCHASE_ORDER_DETAIL_PAGE } from "routes/route.constant";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import { useNotification } from "utils/hooks";

interface PurchaseOrderTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const PurchaseOrderTable: FC<PurchaseOrderTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { notification } = useNotification();
  const { purchaseOrders, pagination, isLoading, mutate } =
    usePurchaseOrders(params);
  const [isActing, setIsActing] = useState(false);

  const handleNavigateToDetail = useCallback(
    (id: string) => {
      navigate(PURCHASE_ORDER_DETAIL_PAGE.replace(":id", id));
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (row: PurchaseOrderResponseType) => {
      navigate(`${PURCHASE_ORDER_DETAIL_PAGE.replace(":id", row.id)}?mode=edit`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row: PurchaseOrderResponseType) => {
      modals.openConfirmModal({
        title: "Xóa đơn mua hàng",
        children: (
          <Text size="sm">
            Bạn có chắc chắn muốn xóa đơn mua hàng{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không?
          </Text>
        ),
        labels: { confirm: "Xóa", cancel: "Đóng" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          setIsActing(true);
          try {
            await purchaseOrderApi.delete(row.id);
            notification.success({ title: "Xóa đơn mua hàng thành công" });
            mutate();
          } catch (error: any) {
            notification.error({
              title: "Xóa đơn mua hàng thất bại",
              message: error?.error?.message,
            });
          } finally {
            setIsActing(false);
          }
        },
      });
    },
    [mutate, notification],
  );

  const rowActions: ActionItem<PurchaseOrderResponseType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "purchase_order:read",
        onClick: (row) => handleNavigateToDetail(row.id),
      },
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "blue",
        permission: "purchase_order:update",
        onClick: handleEdit,
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "purchase_order:delete",
        onClick: handleDelete,
      },
    ],
    [handleNavigateToDetail, handleEdit, handleDelete],
  );

  const hasActions = useMemo(
    () => rowActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [rowActions, ability],
  );

  const columns: MRT_ColumnDef<PurchaseOrderResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã đơn hàng",
      Cell: ({ row }) => (
        <Anchor size="sm" onClick={() => handleNavigateToDetail(row.original.id)}>
          {row.original.code}
        </Anchor>
      ),
    },
    {
      accessorKey: "partner.name",
      header: "Nhà cung cấp",
    },
    {
      accessorKey: "warehouse.name",
      header: "Kho nhập",
    },
    {
      accessorKey: "totalAmount",
      header: "Tổng tiền",
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) => {
        const status = cell.getValue<string>();
        return (
          <CustomBadge
            label={PURCHASE_ORDER_STATUS_LABEL[status] ?? status}
            color={PURCHASE_ORDER_STATUS_COLOR[status] ?? "gray"}
          />
        );
      },
    },
    {
      accessorKey: "createdBy.fullname",
      header: "Người tạo",
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={rowActions} />
            ),
          } satisfies MRT_ColumnDef<PurchaseOrderResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading || isActing}>
      <CustomTable
        columns={columns}
        data={purchaseOrders ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default PurchaseOrderTable;
